import { Space, Tooltip } from "antd";
import { type ReactNode, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNotification } from "~hooks/useNotification.ts";
import "./DataTableCopyableCell.scss";

const COPIED_TOOLTIP_DURATION_MS = 2000;

type Props = {
  copyValue?: string | null;
  children: ReactNode;
};

export const DataTableCopyableCell = ({ copyValue, children }: Props) => {
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeoutId = window.setTimeout(() => setIsCopied(false), COPIED_TOOLTIP_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isCopied]);

  if (!copyValue) {
    return children;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyValue);
      setIsCopied(true);
    } catch {
      notification.error({ message: formatMessage({ id: "copy.error" }), placement: "bottomRight" });
    }
  };

  return (
    <Space size={8} className="copyable-cell">
      {children}
      <Tooltip title={formatMessage({ id: "copied.clipboard" })} open={isCopied}>
        <i
          className={`fa-regular fa-copy copy-icon ${isCopied ? "copied" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            void handleCopy();
          }}
          onAuxClick={(event) => event.stopPropagation()}
        />
      </Tooltip>
    </Space>
  );
};
